/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useRef, useMemo, ComponentProps } from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import {
  Table as DefTable,
  TableBody as DefTableBody,
  TableFooter,
  TableHead as DefTableHead,
  TableRow as DefTableRow,
} from '@mui/material';
import { animated } from '@react-spring/web';
import { getPrice } from '../../utils/currency';
import { semiBoldText } from '../../utils/fonts';
import { getPercentages } from '../../utils/percentages';
import {
  minPortfolioItemCostToRender,
  handlePortfolioTableDataItemEmptyIcon,
} from '../../utils/portfolio';
import { useHorizontalDemoScroll } from '../../hooks';
import {
  PortfolioCurrency,
  LastRebalanceReasonType,
  DashboardDataQuery,
} from '../../apollo';
import { useExchange, usePortfolioCurrency } from '../../providers';
import { Spinner } from '../../styled';
import {
  WrapperWithOverlay,
  OrderCell,
  NameCell,
  PriceCell,
  ChangeCell,
  TokenAmountCell,
  ValueCell,
  PercentageCell,
  TableCell,
  TableRow,
} from '../../styled/portfolio';
import { Text } from '../texts';
import { AumBelowMinimumPopup as DefAumBelowMinimumPopup } from './AumBelowMinimumPopup';
import { BuySellOrderFailedPopup as DefBuySellOrderFailedPopup } from './BuySellOrderFailedPopup';
import { NewRebalancePopup as DefNewRebalancePopup } from './NewRebalancePopup';
import { OrderInProgressPopup as DefOrderInProgressPopup } from './OrderInProgressPopup';
import { PortfolioTableRow, PortfolioTableRowData } from './PortfolioTableRow';
import { RebalanceFailedPopup as DefRebalanceFailedPopup } from './RebalanceFailedPopup';
import { RebalanceInProgressPopup as DefRebalanceInProgressPopup } from './RebalanceInProgressPopup';

export type IndexPortfolioTableProps = {
  portfolio: DashboardDataQuery['getIndexPortfolio'];
} & ComponentProps<'section'>;

const IndexPortfolioTable = ({
  portfolio,
  ...props
}: IndexPortfolioTableProps) => {
  const { t } = useTranslation();
  const { exchange } = useExchange();

  const { currency } = usePortfolioCurrency();

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const rawTableData = useMemo(
    () =>
      portfolio.map(
        ({
          cost,
          usdCost,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          currency: { piechartColor, ...restCurrency },
          ...rest
        }) =>
          ({
            cost,
            usdCost,
            ...restCurrency,
            ...rest,
          }) as PortfolioTableRowData
      ),
    [portfolio]
  );

  const totalPercentToRender = useMemo(
    () =>
      getPercentages(
        rawTableData.reduce((res, { percentage }) => res + percentage, 0),
        {
          round: true,
        }
      ),
    [rawTableData]
  );

  const {
    loading: loadingTableData,
    error,
    value: tableData = [],
  } = useAsync(() => {
    const dataWithoutIcon = rawTableData.filter(
      ({ cost }) => minPortfolioItemCostToRender <= cost
    );
    return handlePortfolioTableDataItemEmptyIcon(dataWithoutIcon);
  }, [rawTableData]);

  const [fiats, cryptos] = useMemo(
    () =>
      tableData.reduce<[PortfolioTableRowData[], PortfolioTableRowData[]]>(
        (acc, data) =>
          data.isFiat
            ? [[...acc[0], data], acc[1]]
            : [acc[0], [...acc[1], data]],
        [[], []]
      ),
    [tableData]
  );

  const rows = useMemo(
    () =>
      [...cryptos, ...fiats].map((data, index) => (
        <PortfolioTableRow key={data.id} data={data} index={index} />
      )),
    [cryptos, fiats]
  );

  const smallTokensData = useMemo(() => {
    const rawData = portfolio
      .filter(({ cost }) => cost < minPortfolioItemCostToRender)
      .reduce(
        (acc, { cost, usdCost, percentage }) => ({
          amount:
            acc.amount + (currency === PortfolioCurrency.Eur ? cost : usdCost),
          percent: acc.percent + percentage,
        }),
        { amount: 0, percent: 0 }
      );

    return rawData.amount >= 0.01
      ? {
          amount: getPrice(
            rawData.amount,
            currency === PortfolioCurrency.Usd
              ? {
                  currency: 'USD',
                }
              : undefined
          ),
          percent: getPercentages(rawData.percent),
        }
      : null;
  }, [currency, portfolio]);

  const portfolioTotalCost = useMemo(() => {
    const costAmount = (portfolio || [])
      .map(({ cost, usdCost }) =>
        currency === PortfolioCurrency.Eur ? cost : usdCost
      )
      .reduce((res, cost) => res + cost, 0);

    return getPrice(
      costAmount,
      currency !== PortfolioCurrency.Eur
        ? {
            currency: 'USD',
          }
        : undefined
    );
  }, [portfolio, currency]);

  const overlayPopup = useMemo(() => {
    const isFailed = () => {
      if (!exchange?.sentToRebalancing) {
        return false;
      }
      return [
        LastRebalanceReasonType.Failed,
        LastRebalanceReasonType.FailedInit,
        LastRebalanceReasonType.FailedReinit,
        LastRebalanceReasonType.FailedUserIsLocked,
        LastRebalanceReasonType.FailedTransactions,
      ].some((reason) => reason === exchange?.lastRebalanceReasonResponse);
    };

    const isPortfolioUpdating = () => {
      if (!exchange?.sentToRebalancing) {
        return false;
      }
      return [
        LastRebalanceReasonType.Starttrial,
        LastRebalanceReasonType.Scheduled,
        LastRebalanceReasonType.Fromdemo,
        LastRebalanceReasonType.Manual,
      ].some((reason) => reason === exchange?.lastRebalanceReasonRequest);
    };

    const isBuySellOrderInProgress = () => {
      if (!exchange?.sentToRebalancing) {
        return false;
      }
      return [
        LastRebalanceReasonType.Deposit,
        LastRebalanceReasonType.Liquidate,
      ].some((reason) => reason === exchange?.lastRebalanceReasonRequest);
    };

    switch (true) {
      case isFailed():
        return [
          LastRebalanceReasonType.Deposit,
          LastRebalanceReasonType.Liquidate,
        ].some((reason) => reason === exchange?.lastRebalanceReasonRequest) ? (
          <BuySellOrderFailedPopup />
        ) : (
          <RebalanceFailedPopup />
        );
      case isPortfolioUpdating():
        return <RebalanceInProgressPopup />;
      case isBuySellOrderInProgress():
        return <OrderInProgressPopup />;
      case exchange?.aumBelowMinimum:
        return <AumBelowMinimumPopup />;
      case exchange?.portfolioOutdated:
        return <NewRebalancePopup />;
      default:
        return null;
    }
  }, [exchange]);

  const scroll = useHorizontalDemoScroll(tableWrapperRef, {
    disabled: !!overlayPopup,
  });

  return (
    <Wrapper $overlayed={!!overlayPopup} {...props}>
      <TableWrapper ref={tableWrapperRef} scrollLeft={scroll}>
        {(() => {
          if (loadingTableData && isEmpty(tableData)) {
            return (
              <Spinner
                style={{
                  margin: 'auto',
                }}
              />
            );
          }

          if (error) {
            return (
              <Text
                style={{
                  width: '100%',
                }}
                align={'center'}
              >
                Error
              </Text>
            );
          }

          return (
            <Table>
              <TableHead>
                <DefTableRow>
                  <OrderCell />
                  <NameCell>
                    {t('DASHBOARD_PAGE__portfolioNameColumn')}
                  </NameCell>
                  <PriceCell>
                    {t('DASHBOARD_PAGE__portfolioPriceColumn')}
                  </PriceCell>
                  <ChangeCell>
                    {t('DASHBOARD_PAGE__portfolioChangeColumn')}
                  </ChangeCell>
                  <TokenAmountCell>
                    {t('DASHBOARD_PAGE__portfolioCoinsColumn')}
                  </TokenAmountCell>
                  <ValueCell>
                    {t('DASHBOARD_PAGE__portfolioValueColumn')}
                  </ValueCell>
                  <PercentageCell>
                    {t('DASHBOARD_PAGE__portfolioPercentageColumn')}
                  </PercentageCell>
                </DefTableRow>
              </TableHead>
              <TableBody>
                {rows}
                {!!smallTokensData && (
                  <TableRow>
                    <OrderCell />
                    <NameCell $lineHeight={1}>
                      {t('DASHBOARD_PAGE__portfolioSmallTokensSumTitle')}
                    </NameCell>
                    <TableCell colSpan={3} />
                    <ValueCell>{smallTokensData.amount}</ValueCell>
                    <PercentageCell>{smallTokensData.percent}</PercentageCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <DefTableRow>
                  <OrderCell />
                  <NameCell>{t('DASHBOARD_PAGE__portfolioTotal')}</NameCell>
                  <TableCell colSpan={3} />
                  <ValueCell>{portfolioTotalCost}</ValueCell>
                  <PercentageCell>{totalPercentToRender}</PercentageCell>
                </DefTableRow>
              </TableFooter>
            </Table>
          );
        })()}
      </TableWrapper>
      {overlayPopup}
    </Wrapper>
  );
};

const Table = styled(DefTable)`
  min-width: 100%;
  width: auto;
  align-self: start;
  flex: 1 1 auto;
  table-layout: fixed;

  /*noinspection CssUnusedSymbol*/
  .MuiTableCell-root {
    border-bottom: none;
  }

  /*noinspection CssUnusedSymbol*/
  .MuiTableCell-footer {
    ${semiBoldText};
  }
`;

const Wrapper = styled(WrapperWithOverlay)`
  ${({ $overlayed }) =>
    $overlayed &&
    css`
      min-height: 340px;

      ${Table} {
        pointer-events: none;
      }
    `}
`;

const TableWrapper = styled(animated.div)`
  display: flex;
  overflow-x: auto;
`;

const TableHead = styled(DefTableHead)`
  /*noinspection CssUnusedSymbol*/
  .MuiTableCell-head {
    padding-top: 14px;
    padding-bottom: 14px;
    border-bottom: none;
    ${semiBoldText};
  }
`;

const TableBody = styled(DefTableBody)``;

const popupStyles = css`
  transform: translateX(-50%);
  position: absolute;
  left: 50%;
  top: 10%;
  z-index: 3;
`;

const NewRebalancePopup = styled(DefNewRebalancePopup)`
  ${popupStyles};
`;

const RebalanceInProgressPopup = styled(DefRebalanceInProgressPopup)`
  ${popupStyles};
`;

const OrderInProgressPopup = styled(DefOrderInProgressPopup)`
  ${popupStyles};
`;

const RebalanceFailedPopup = styled(DefRebalanceFailedPopup)`
  ${popupStyles};
`;

const BuySellOrderFailedPopup = styled(DefBuySellOrderFailedPopup)`
  ${popupStyles};
`;

const AumBelowMinimumPopup = styled(DefAumBelowMinimumPopup)`
  ${popupStyles};
  padding-left: 22px;
  padding-right: 22px;
`;

export { IndexPortfolioTable };
